'use client';

import { API_ROUTES, ApiError, useApiService } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Button, cn } from '@shared/components/shadcn';
import { Deposit } from '@shared/icons/system';
import { buildPlayerTrackerImpressionPayload, PLAYER_TRACKER_IMPRESSION, usePlayerTrackerServiceEvent } from '@shared/tracking';
import { useClientConfig } from '@shared/utils';
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { toast } from '../../../../Toast/Toast';
import { MyAccountSheet } from '../../../MyAccountSheet/MyAccountSheet';

type FinancialStatementSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentClassName?: string;
  trigger: ReactNode;
};

type RequestFinancialStatementPayload = {
  year: number;
  month: number;
};

function getPreviousCalendarMonth(referenceDate = new Date()): RequestFinancialStatementPayload {
  const previousMonthDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - 1, 1);

  return {
    year: previousMonthDate.getFullYear(),
    month: previousMonthDate.getMonth() + 1,
  };
}

const FINANCIAL_STATEMENT_TOAST_TRACKING = {
  success_toast: {
    type: 'success' as const,
    'data-track-type': 'Confirmation',
    'data-track-action': 'Financial Statement - Requested',
  },
  recently_requested_toast: {
    type: 'error' as const,
    'data-track-type': 'Failure',
    'data-track-action': 'Financial Statement - Already Requested',
  },
  unavailable_toast: {
    type: 'error' as const,
    'data-track-type': 'Failure',
    'data-track-action': 'Financial Statement - Not Available',
  },
  error_toast: {
    type: 'error' as const,
    'data-track-type': 'Failure',
    'data-track-action': 'Financial Statement - Something Wrong',
  },
};

type FinancialStatementToastKey = keyof typeof FINANCIAL_STATEMENT_TOAST_TRACKING;

function getFinancialStatementErrorToastKey(error: unknown): Exclude<FinancialStatementToastKey, 'success_toast'> {
  if (!(error instanceof ApiError)) {
    return 'error_toast';
  }

  if (error.getStatus() === 403) {
    return 'unavailable_toast';
  }

  if (error.getStatus() === 429) {
    return 'recently_requested_toast';
  }

  return 'error_toast';
}

function FinancialStatementSheet({ open, onOpenChange, contentClassName, trigger }: FinancialStatementSheetProps) {
  const t = useTranslations('my_account.wallet.financial_statement_sheet');
  const ptsTrackEvent = usePlayerTrackerServiceEvent();
  const { apiKey } = useClientConfig();
  const apiService = useApiService();
  const { session } = useSession();
  const playerId = session.profile?.id;
  const { mutate, isPending } = useMutation({
    mutationFn: async (payload: RequestFinancialStatementPayload) => {
      const response = await apiService.post<void>(API_ROUTES.MY_ACCOUNT.WALLET.REQUEST_FINANCIAL_STATEMENT, payload, {
        credentials: 'include',
        headers: {
          'x-api-key': apiKey,
        },
      });

      if (response.error) {
        throw response.error;
      }

      return response.status;
    },
  });

  const showFinancialStatementToast = (toastKey: FinancialStatementToastKey) => {
    const { type, ...tracking } = FINANCIAL_STATEMENT_TOAST_TRACKING[toastKey];

    toast(t(toastKey), { type }, { 'data-track-container': 'Toast', ...tracking });
  };

  const handleClick = () => {
    if (isPending) {
      return;
    }

    ptsTrackEvent(buildPlayerTrackerImpressionPayload(PLAYER_TRACKER_IMPRESSION.REQUEST_FINANCIAL_STATEMENT, playerId ? { id: playerId } : undefined));

    mutate(getPreviousCalendarMonth(), {
      onSuccess: status => {
        if (status === 204) {
          showFinancialStatementToast('recently_requested_toast');
          return;
        }

        showFinancialStatementToast('success_toast');
        onOpenChange(false);
      },
      onError: error => {
        showFinancialStatementToast(getFinancialStatementErrorToastKey(error));
      },
    });
  };

  return (
    <MyAccountSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('sheet_title')}
      contentClassName={cn(contentClassName, 'max-w-md')}
      trigger={trigger}
      data-testid="financial-statement"
      data-track-feature="My Account"
      data-track-container="Financial Statement"
    >
      <div className="flex flex-col p-6 pt-8 gap-8">
        <div className="flex flex-col gap-5 items-start justify-start w-full">
          <Deposit size="extra-extra-large" data-testid="deposit-icon" />
          <h4>{t('title')}</h4>
        </div>

        <div className="flex flex-col gap-2.5">
          <p>{t('description')}</p>
          <p>{t('list_title')}</p>
          <ul className="list-disc list-outside pl-5" data-testid="financial-statement-eligibility-list">
            <li>{t('list_item_1')}</li>
            <li>{t('list_item_2')}</li>
          </ul>
          <p>{t('instant_access_description')}</p>
        </div>

        <Button onClick={handleClick} className="w-full" data-track-name="Request Statement" data-testid="financial-statement-request-button">
          {t('request_button')}
        </Button>
      </div>
    </MyAccountSheet>
  );
}

export { FinancialStatementSheet };
export type { FinancialStatementSheetProps, RequestFinancialStatementPayload };
